/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Who is carrying what, right now.
 *
 * The number that matters is capacity, not item count: an agent on one call is
 * busier than one holding two chats, and a list of row counts says the
 * opposite. Where a tenant has not switched capacity on, there is no ceiling to
 * measure against -- so this shows a count instead of a bar rather than drawing
 * a progress bar against infinity, which would be a lie.
 *
 * Scoped server-side to the queues the viewer supervises. A supervisor of the
 * sales queue has no business reading how loaded the support team is.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";

const CARD_BORDER = "1px solid #E6E8F0";

const STATE_COLOUR = {
  available: "success",
  on_call: "info",
  ringing: "warning",
  wrap_up: "warning",
  busy: "error",
  offline: "default",
};

function utilisationColour(value) {
  if (value === null || value === undefined) return "info";
  if (value >= 0.9) return "error";
  if (value >= 0.7) return "warning";
  return "success";
}

function AgentRow({ agent, onReconcile, busy }) {
  const capped = agent.max_capacity !== null && agent.max_capacity !== undefined;
  const pct = capped ? Math.min(100, Math.round((agent.utilisation || 0) * 100)) : null;

  return (
    <MDBox sx={{ borderBottom: "1px solid #F2F4F8", py: 1.5 }}>
      <MDBox display="flex" alignItems="center" gap={1.5}>
        <MDBox flexGrow={1} minWidth={0}>
          <MDTypography variant="button" fontWeight="medium" noWrap display="block">
            {agent.display_name || `User ${agent.user_id}`}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {agent.open_items} item{agent.open_items === 1 ? "" : "s"}
            {agent.active_voice_count ? ` · ${agent.active_voice_count} on call` : ""}
            {agent.device_status !== "registered" ? " · no softphone" : ""}
          </MDTypography>
        </MDBox>

        <Chip
          size="small"
          variant={agent.state === "available" ? "filled" : "outlined"}
          color={STATE_COLOUR[agent.state] || "default"}
          label={agent.state.replace("_", " ")}
        />

        {capped ? (
          <MDBox minWidth={140}>
            <MDTypography variant="caption" color="text" display="block" textAlign="right">
              {agent.current_capacity} / {agent.max_capacity}
            </MDTypography>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={utilisationColour(agent.utilisation)}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </MDBox>
        ) : (
          <Tooltip title="This tenant has no capacity ceiling set, so there is nothing to measure against.">
            <Chip
              size="small"
              variant="outlined"
              label={`${agent.current_capacity} units · uncapped`}
            />
          </Tooltip>
        )}

        <Tooltip title="Recount this agent's load from the work they actually hold.">
          <span>
            <MDButton
              variant="text"
              color="dark"
              size="small"
              disabled={busy}
              onClick={() => onReconcile(agent)}
            >
              Recount
            </MDButton>
          </span>
        </Tooltip>
      </MDBox>
    </MDBox>
  );
}

function AgentWorkloadPage() {
  const { axiosInstance } = useAuth();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [agents, setAgents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [queueFilter, setQueueFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/work/agents/workload", {
        params: queueFilter ? { queue_id: queueFilter } : {},
      });
      setAgents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load agent workload."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, queueFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Only feeds the filter; a failure here must not hide the agents.
    axiosInstance
      .get("/work/queues")
      .then((res) => setQueues(Array.isArray(res.data) ? res.data : []))
      .catch(() => setQueues([]));
  }, [axiosInstance]);

  const reconcile = async (agent) => {
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance.post(`/work/agents/${agent.user_id}/reconcile`);
      setAgents((rows) => rows.map((r) => (r.user_id === agent.user_id ? res.data : r)));
      setToast(
        res.data.current_capacity === agent.current_capacity
          ? "Already correct."
          : `Corrected to ${res.data.current_capacity}.`
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not recount that agent."));
    } finally {
      setBusy(false);
    }
  };

  const uncapped = agents.length > 0 && agents.every((a) => a.max_capacity == null);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <MDBox flexGrow={1}>
            <MDTypography variant="h5" fontWeight="medium">
              Agent workload
            </MDTypography>
            <MDTypography variant="body2" color="text">
              How much each agent is carrying across calls, cases and messages.
            </MDTypography>
          </MDBox>
          <TextField
            select
            size="small"
            label="Queue"
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All my queues</MenuItem>
            {queues.map((q) => (
              <MenuItem key={q.queue_id} value={q.queue_id}>
                {q.name}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </MDBox>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {uncapped ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No capacity ceiling is set for this organisation, so nothing is being limited yet.
            Make a capacity profile the default in Settings → Queues → Capacity to switch it on.
          </Alert>
        ) : null}

        <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </MDBox>
          ) : agents.length === 0 ? (
            <MDTypography variant="body2" color="text">
              No agents to show. You see the agents in the queues you supervise, plus yourself.
            </MDTypography>
          ) : (
            <>
              <MDTypography variant="button" fontWeight="medium" color="text">
                Busiest first
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              {agents.map((agent) => (
                <AgentRow
                  key={agent.user_id}
                  agent={agent}
                  busy={busy}
                  onReconcile={reconcile}
                />
              ))}
            </>
          )}
        </Card>
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}

export default AgentWorkloadPage;
